import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Input from "@mui/material/Input";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectNordPoolPriceSteps } from "store/selectors";
import { fetchNordPoolPriceSteps } from "store/appSlice";
import {
  fifteenMinutePeriods,
  fifteenMinutePeriodStarts,
} from "helpers/periods";
import { tallinnTimeToUtc } from "helpers/timeIntervals";
import { getAuctionPriceLimitBounds } from "helpers/auctions";

// Snap to nearest admin-defined step. On a tie, prefer the step closer to 0.
const snapToStep = (value: number, steps: number[]): number => {
  if (steps.length === 0) return value;
  let best = steps[0];
  let bestDist = Math.abs(value - best);
  for (let i = 1; i < steps.length; i++) {
    const step = steps[i];
    const dist = Math.abs(value - step);
    if (dist < bestDist) {
      best = step;
      bestDist = dist;
    } else if (dist === bestDist && Math.abs(step) < Math.abs(best)) {
      best = step;
    }
  }
  return best;
};

interface PeriodPriceLimits {
  time: string;
  buy: number | string;
  sell: number | string;
}

interface PriceLimitsTableProps {
  periodPriceLimits: PeriodPriceLimits[];
  setPeriodPriceLimits: (periodPriceLimits: PeriodPriceLimits[]) => void;
  portfolioPriceLimits: PeriodPriceLimits[];
  setValuesChanged: any;
  selectedAuction: number;
}

const PriceLimitsTable = ({
  periodPriceLimits,
  setPeriodPriceLimits,
  portfolioPriceLimits,
  setValuesChanged,
  selectedAuction,
}: PriceLimitsTableProps) => {
  const dispatch = useAppDispatch();
  const priceSteps = useAppSelector(selectNordPoolPriceSteps);
  const hasSteps = priceSteps.length > 0;
  const auctionBounds = getAuctionPriceLimitBounds(selectedAuction);

  const [snapConfirm, setSnapConfirm] = useState<{
    rowIndex: number;
    field: "buy" | "sell";
    original: number;
    snapped: number;
    anchor: HTMLElement;
  } | null>(null);
  const [stepsAnchor, setStepsAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    dispatch(fetchNordPoolPriceSteps());
  }, [dispatch]);

  const requestSnapConfirm = (
    rowIndex: number,
    field: "buy" | "sell",
    anchor: HTMLElement | null,
  ) => {
    if (!hasSteps || !anchor) return;
    const row = periodPriceLimits[rowIndex];
    if (!row) return;
    const current = row[field];
    if (current === "" || current === null || current === undefined) return;
    const numeric = Number(current);
    if (!Number.isFinite(numeric)) return;
    // Auction min/max are always valid snap targets, in addition to the
    // admin-defined price steps.
    const snapCandidates = Array.from(
      new Set([...priceSteps, auctionBounds.min, auctionBounds.max]),
    );
    const snapped = snapToStep(numeric, snapCandidates);
    if (snapped === numeric) return;

    setSnapConfirm({ rowIndex, field, original: numeric, snapped, anchor });
  };

  const confirmSnap = () => {
    if (!snapConfirm) return;
    const { rowIndex, field, snapped } = snapConfirm;
    const next = [...periodPriceLimits];
    next[rowIndex] = { ...next[rowIndex], [field]: snapped };
    setPeriodPriceLimits(next);
    setValuesChanged(true);
    setSnapConfirm(null);
  };

  const generateTable = () => {
    const periodPriceLimitsCopy = fifteenMinutePeriodStarts.map(
      (period, periodIndex) => {
        const utcTime = tallinnTimeToUtc(
          fifteenMinutePeriodStarts[periodIndex],
        );
        return {
          time: period,
          buy:
            portfolioPriceLimits?.find((pl) => pl.time === utcTime)?.buy ?? "",
          sell:
            portfolioPriceLimits?.find((pl) => pl.time === utcTime)?.sell ?? "",
        };
      },
    );

    setPeriodPriceLimits(periodPriceLimitsCopy);
  };

  useEffect(() => {
    if (portfolioPriceLimits) {
      generateTable();
    }
  }, [portfolioPriceLimits]);

  const firstEditableRow = periodPriceLimits[4];
  const showApplySell = firstEditableRow && firstEditableRow.sell !== "";
  const showApplyBuy = firstEditableRow && firstEditableRow.buy !== "";

  const handleApplySellToAll = () => {
    const updated = periodPriceLimits.map((row, index) => {
      if (index < 4) return row;
      return { ...row, sell: firstEditableRow.sell };
    });
    setPeriodPriceLimits(updated);
    setValuesChanged(true);
  };

  const handleApplyBuyToAll = () => {
    const updated = periodPriceLimits.map((row, index) => {
      if (index < 4) return row;
      return { ...row, buy: firstEditableRow.buy };
    });
    setPeriodPriceLimits(updated);
    setValuesChanged(true);
  };

  return (
    <Box sx={{ maxWidth: 480 }}>
      {hasSteps && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            Values change to {priceSteps.length} predefined step
            {priceSteps.length === 1 ? "" : "s"} ({priceSteps[0]} –{" "}
            {priceSteps[priceSteps.length - 1]} €).
          </Typography>
          <Button
            size="small"
            onClick={(e) => setStepsAnchor(e.currentTarget)}
            sx={{
              minWidth: 0,
              p: 0,
              textTransform: "none",
              fontSize: "0.75rem",
            }}
          >
            View all
          </Button>
          <Popover
            open={Boolean(stepsAnchor)}
            anchorEl={stepsAnchor}
            onClose={() => setStepsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box
              sx={{
                p: 1.5,
                maxWidth: 360,
                maxHeight: 320,
                overflowY: "auto",
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              {priceSteps.map((s) => (
                <Chip key={s} label={s.toString()} size="small" />
              ))}
            </Box>
          </Popover>
        </Box>
      )}
      <Popover
        open={Boolean(snapConfirm)}
        anchorEl={snapConfirm?.anchor ?? null}
        onClose={() => setSnapConfirm(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
      >
        <Box
          sx={{
            p: 1.5,
            maxWidth: 280,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="body2">
            <strong>{snapConfirm?.original}</strong> isn&apos;t a predefined
            step. It will be changed to <strong>{snapConfirm?.snapped}</strong>.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button size="small" variant="contained" onClick={confirmSnap}>
              OK
            </Button>
          </Box>
        </Box>
      </Popover>
      <TableContainer component={Paper} sx={{ maxWidth: 480 }}>
        <Table sx={{ maxWidth: 480 }} size="small">
          <TableHead sx={{ bgcolor: "#eeeeee" }}>
            <TableRow>
              <TableCell>Period (Europe/Tallinn)</TableCell>
              <TableCell>Sell (€)</TableCell>
              <TableCell>Buy (€)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periodPriceLimits.map((row, rowIndex) => (
              <TableRow key={"Row-" + rowIndex}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography component="p">
                      {fifteenMinutePeriods[rowIndex]}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl variant="standard">
                      <Input
                        id="sell"
                        type="number"
                        size="small"
                        placeholder={String(auctionBounds.min)}
                        inputProps={{
                          min: auctionBounds.min,
                          max: auctionBounds.max,
                        }}
                        value={row.sell ?? ""}
                        disabled={rowIndex < 4}
                        sx={{
                          maxWidth: "110px",
                          backgroundColor:
                            snapConfirm?.rowIndex === rowIndex &&
                            snapConfirm?.field === "sell"
                              ? "rgba(237, 108, 2, 0.16)"
                              : "transparent",
                          transition: "background-color 200ms ease-out",
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <ChevronRightIcon />
                          </InputAdornment>
                        }
                        onChange={(e) => {
                          const periodPriceLimitsCopy: PeriodPriceLimits[] = [
                            ...periodPriceLimits,
                          ];

                          periodPriceLimitsCopy[rowIndex].sell =
                            e.target.value === ""
                              ? ""
                              : Number(e.target.value);

                          setPeriodPriceLimits(periodPriceLimitsCopy);
                          setValuesChanged(true);
                        }}
                        onBlur={(e) =>
                          requestSnapConfirm(rowIndex, "sell", e.currentTarget)
                        }
                      />
                    </FormControl>
                    <Box sx={{ width: 24, flexShrink: 0 }}>
                      {rowIndex === 4 && showApplySell && (
                        <Tooltip title="Apply to all">
                          <IconButton
                            size="small"
                            onClick={handleApplySellToAll}
                            sx={{ p: 0.25 }}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <FormControl variant="standard">
                      <Input
                        id="buy"
                        type="number"
                        size="small"
                        placeholder={String(auctionBounds.max)}
                        inputProps={{
                          min: auctionBounds.min,
                          max: auctionBounds.max,
                        }}
                        value={row.buy ?? ""}
                        disabled={rowIndex < 4}
                        sx={{
                          maxWidth: "110px",
                          backgroundColor:
                            snapConfirm?.rowIndex === rowIndex &&
                            snapConfirm?.field === "buy"
                              ? "rgba(237, 108, 2, 0.16)"
                              : "transparent",
                          transition: "background-color 200ms ease-out",
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <ChevronLeftIcon />
                          </InputAdornment>
                        }
                        onChange={(e) => {
                          const periodPriceLimitsCopy: PeriodPriceLimits[] = [
                            ...periodPriceLimits,
                          ];

                          periodPriceLimitsCopy[rowIndex].buy =
                            e.target.value === ""
                              ? ""
                              : Number(e.target.value);

                          setPeriodPriceLimits(periodPriceLimitsCopy);
                          setValuesChanged(true);
                        }}
                        onBlur={(e) =>
                          requestSnapConfirm(rowIndex, "buy", e.currentTarget)
                        }
                      />
                    </FormControl>
                    <Box sx={{ width: 24, flexShrink: 0 }}>
                      {rowIndex === 4 && showApplyBuy && (
                        <Tooltip title="Apply to all">
                          <IconButton
                            size="small"
                            onClick={handleApplyBuyToAll}
                            sx={{ p: 0.25 }}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PriceLimitsTable;
